import {
  Box,
  TextField,
  Button,
  Typography,
  Grid,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  DialogContentText,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import ArticleIcon from "@mui/icons-material/Article";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createTicket } from "../services/api";
import SnackbarAlert from "./SnackbarAlert";

const NAME_MAX_LENGTH = 50;
const PHONE_MAX_LENGTH = 50;
const EMAIL_MAX_LENGTH = 50;
const ADDRESS_FIELD_MAX_LENGTH = 50;
const NAME_REGEX = /^[a-zA-Zא-ת\s'-]*$/;
const PHONE_REGEX = /^[0-9]*$/;
const EMAIL_FORMAT_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ADDRESS_FIELDS = [
  { key: "city", label: "עיר", isNumeric: false, required: true },
  { key: "street", label: "רחוב", isNumeric: false, required: true },
  { key: "number", label: "מספר", isNumeric: true, required: true },
  { key: "floor", label: "קומה", isNumeric: true, required: false },
  { key: "room", label: "חדר", isNumeric: true, required: false },
];

function OpeningReading({ data }) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    text: "",
    urgency: "",
    callType: "",
    images: [],
    confirmAddress: false,
    confirmContactPerson: false,
    termsAccepted: false,
  });

  const [fieldErrors, setFieldErrors] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const [deleteConfirm, setDeleteConfirm] = useState({
    open: false,
    imageIndex: null,
  });

  const [addressDialogOpen, setAddressDialogOpen] = useState(false);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [addressChangeRequest, setAddressChangeRequest] = useState({
    city: "",
    street: "",
    number: "",
    floor: "",
    room: "",
  });
  const [addressFieldErrors, setAddressFieldErrors] = useState({
    city: "",
    street: "",
    number: "",
    floor: "",
    room: "",
  });
  const [contactNameChangeRequest, setContactNameChangeRequest] = useState("");
  const [contactPhoneChangeRequest, setContactPhoneChangeRequest] = useState("");
  const [contactEmailChangeRequest, setContactEmailChangeRequest] = useState("");
  const [contactFieldErrors, setContactFieldErrors] = useState({
    contactName: "",
    contactPhone: "",
    contactEmail: "",
  });

  useEffect(() => {
    // Cleanup object URLs when component unmounts or images change
    return () => {
      formData.images.forEach((image) => {
        if (image instanceof File) {
          URL.revokeObjectURL(URL.createObjectURL(image));
        }
      });
    };
  }, [formData.images]);

  const handleChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
  };

  const handleNameChange = (field, label) => (event) => {
    const value = event.target.value;
    if (value.length > NAME_MAX_LENGTH) {
      setFieldErrors((prev) => ({
        ...prev,
        [field]: `ניתן להזין עד 50 תווים בשדה ${label}`,
      }));
      return;
    }
    if (!NAME_REGEX.test(value)) {
      setFieldErrors((prev) => ({
        ...prev,
        [field]: `שדה ${label} יכול להכיל אותיות בלבד`,
      }));
      return;
    }
    setFormData({
      ...formData,
      [field]: value,
    });
    setFieldErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handlePhoneChange = (event) => {
    const value = event.target.value;
    if (value.length > PHONE_MAX_LENGTH) {
      setFieldErrors((prev) => ({
        ...prev,
        phone: "ניתן להזין עד 50 תווים בשדה טלפון",
      }));
      return;
    }
    if (!PHONE_REGEX.test(value)) {
      setFieldErrors((prev) => ({
        ...prev,
        phone: "שדה טלפון יכול להכיל מספרים בלבד",
      }));
      return;
    }
    setFormData({
      ...formData,
      phone: value,
    });
    setFieldErrors((prev) => ({ ...prev, phone: "" }));
  };

  const handleEmailChange = (event) => {
    const value = event.target.value;
    if (value.length > EMAIL_MAX_LENGTH) {
      setFieldErrors((prev) => ({
        ...prev,
        email: "ניתן להזין עד 50 תווים בשדה אימייל",
      }));
      return;
    }
    setFormData({
      ...formData,
      email: value,
    });
    setFieldErrors((prev) => ({ ...prev, email: "" }));
  };

  const handleContactNameChangeInput = (event) => {
    const value = event.target.value;
    if (value.length > NAME_MAX_LENGTH) {
      setContactFieldErrors((prev) => ({
        ...prev,
        contactName: "ניתן להזין עד 50 תווים בשדה שם איש קשר",
      }));
      return;
    }
    if (!NAME_REGEX.test(value)) {
      setContactFieldErrors((prev) => ({
        ...prev,
        contactName: "שדה שם איש קשר יכול להכיל אותיות בלבד",
      }));
      return;
    }
    setContactNameChangeRequest(value);
    setContactFieldErrors((prev) => ({ ...prev, contactName: "" }));
  };

  const handleContactPhoneChangeInput = (event) => {
    const value = event.target.value;
    if (value.length > PHONE_MAX_LENGTH) {
      setContactFieldErrors((prev) => ({
        ...prev,
        contactPhone: "ניתן להזין עד 50 תווים בשדה טלפון",
      }));
      return;
    }
    if (!PHONE_REGEX.test(value)) {
      setContactFieldErrors((prev) => ({
        ...prev,
        contactPhone: "שדה טלפון יכול להכיל מספרים בלבד",
      }));
      return;
    }
    setContactPhoneChangeRequest(value);
    setContactFieldErrors((prev) => ({ ...prev, contactPhone: "" }));
  };

  const handleContactEmailChangeInput = (event) => {
    const value = event.target.value;
    if (value.length > EMAIL_MAX_LENGTH) {
      setContactFieldErrors((prev) => ({
        ...prev,
        contactEmail: "ניתן להזין עד 50 תווים בשדה אימייל",
      }));
      return;
    }
    setContactEmailChangeRequest(value);
    setContactFieldErrors((prev) => ({ ...prev, contactEmail: "" }));
  };

  const handleCheckboxChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.checked,
    });
  };

  const handleAddressFieldChange = (field, label, isNumeric) => (event) => {
    const value = event.target.value;
    if (value.length > ADDRESS_FIELD_MAX_LENGTH) {
      setAddressFieldErrors((prev) => ({
        ...prev,
        [field]: `ניתן להזין עד ${ADDRESS_FIELD_MAX_LENGTH} תווים בשדה ${label}`,
      }));
      return;
    }
    const regex = isNumeric ? PHONE_REGEX : NAME_REGEX;
    if (!regex.test(value)) {
      setAddressFieldErrors((prev) => ({
        ...prev,
        [field]: `שדה ${label} יכול להכיל ${isNumeric ? "מספרים" : "אותיות"} בלבד`,
      }));
      return;
    }
    setAddressChangeRequest((prev) => ({ ...prev, [field]: value }));
    setAddressFieldErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleAddressDialogCancel = () => {
    setAddressDialogOpen(false);
    setAddressChangeRequest({ city: "", street: "", number: "", floor: "", room: "" });
    setAddressFieldErrors({ city: "", street: "", number: "", floor: "", room: "" });
  };

  const handleAddressDialogSave = () => {
    const missingRequiredErrors = {};
    ADDRESS_FIELDS.forEach(({ key, required }) => {
      if (required && !addressChangeRequest[key].trim()) {
        missingRequiredErrors[key] = "שדה חובה";
      }
    });

    if (Object.keys(missingRequiredErrors).length > 0) {
      setAddressFieldErrors((prev) => ({ ...prev, ...missingRequiredErrors }));
      return;
    }

    setAddressDialogOpen(false);
    setFormData((prev) => ({ ...prev, confirmAddress: false }));
  };

  const handleContactDialogCancel = () => {
    setContactDialogOpen(false);
    setContactNameChangeRequest("");
    setContactPhoneChangeRequest("");
    setContactEmailChangeRequest("");
    setContactFieldErrors({ contactName: "", contactPhone: "", contactEmail: "" });
  };

  const handleContactDialogSave = () => {
    const missingRequiredErrors = {};
    if (!contactNameChangeRequest.trim()) {
      missingRequiredErrors.contactName = "שדה חובה";
    }
    if (!contactPhoneChangeRequest.trim()) {
      missingRequiredErrors.contactPhone = "שדה חובה";
    }
    if (!contactEmailChangeRequest.trim()) {
      missingRequiredErrors.contactEmail = "שדה חובה";
    }

    if (Object.keys(missingRequiredErrors).length > 0) {
      setContactFieldErrors((prev) => ({ ...prev, ...missingRequiredErrors }));
      return;
    }

    setContactDialogOpen(false);
    setFormData((prev) => ({ ...prev, confirmContactPerson: false }));
    setContactFieldErrors({ contactName: "", contactPhone: "", contactEmail: "" });
  };

  const handleImageUpload = (event) => {
    const files = Array.from(event.target.files);
    const remainingSlots = 5 - formData.images.length;

    if (remainingSlots <= 0) {
      setSnackbar({
        open: true,
        message: "ניתן להעלות מקסימום 5 תמונות",
        severity: "warning",
      });
      event.target.value = "";
      return;
    }

    // Take only the number of files that will fit within the limit
    const filesToAdd = files.slice(0, remainingSlots);

    setFormData((prev) => ({
      ...prev,
      images: [...prev.images, ...filesToAdd],
    }));

    if (files.length > remainingSlots) {
      setSnackbar({
        open: true,
        message: `נבחרו רק ${remainingSlots} תמונות מתוך ${files.length} בגלל המגבלה של 5 תמונות`,
        severity: "info",
      });
    }

    event.target.value = "";
  };

  const handleImageClick = (index) => {
    setDeleteConfirm({
      open: true,
      imageIndex: index,
    });
  };

  const handleDeleteConfirm = () => {
    if (deleteConfirm.imageIndex !== null) {
      setFormData((prev) => ({
        ...prev,
        images: prev.images.filter(
          (_, index) => index !== deleteConfirm.imageIndex
        ),
      }));
    }
    setDeleteConfirm({
      open: false,
      imageIndex: null,
    });
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm({
      open: false,
      imageIndex: null,
    });
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleSave = async () => {
    if (
      !formData.firstName ||
      !formData.lastName ||
      !formData.phone ||
      !formData.text ||
      !formData.callType ||
      !formData.urgency ||
      !formData.email
    ) {
      setSnackbar({
        open: true,
        message: "אנא מלא את כל השדות הנדרשים",
        severity: "error",
      });
      return;
    }

    if (!EMAIL_FORMAT_REGEX.test(formData.email)) {
      setSnackbar({
        open: true,
        message: "שדה אימייל יכול להכיל כתובת מייל תקינה בלבד",
        severity: "error",
      });
      return;
    }

    const addressHasValue = ADDRESS_FIELDS.some(
      ({ key }) => addressChangeRequest[key].trim()
    );

    if (!formData.confirmAddress && !addressHasValue) {
      setSnackbar({
        open: true,
        message: "יש לאשר שכתובת האתר נכונה או לעדכן אותה",
        severity: "error",
      });
      return;
    }

    if (
      !formData.confirmContactPerson &&
      !(contactNameChangeRequest.trim() && contactPhoneChangeRequest.trim())
    ) {
      setSnackbar({
        open: true,
        message: "יש לאשר שאיש הקשר נכון או לעדכן אותו",
        severity: "error",
      });
      return;
    }

    if (!formData.termsAccepted) {
      setSnackbar({
        open: true,
        message: "יש לאשר את תנאי השימוש ומדיניות הפרטיות כדי להמשיך",
        severity: "error",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const fullName = `${formData.firstName} ${formData.lastName}`.trim();
      const formattedAddressChangeRequest = ADDRESS_FIELDS.filter(
        ({ key }) => addressChangeRequest[key].trim()
      )
        .map(({ key, label }) => `${label}: ${addressChangeRequest[key].trim()}`)
        .join(", ");
      const ticketData = {
        serialNumber: data.SERNUM,
        customerNumber: data.CUSTNAME,
        callType: formData.callType,
        updateSource: fullName,
        contactInfo: formData.phone,
        description: formData.text,
        urgency: formData.urgency,
        email: formData.email,
        images: formData.images,
        addressChangeRequest: formData.confirmAddress ? "" : formattedAddressChangeRequest,
        contactNameChangeRequest: formData.confirmContactPerson ? "" : contactNameChangeRequest,
        contactPhoneChangeRequest: formData.confirmContactPerson ? "" : contactPhoneChangeRequest,
        contactEmailChangeRequest: formData.confirmContactPerson ? "" : contactEmailChangeRequest,
      };

      const response = await createTicket(ticketData);

      navigate("/successful", {
        state: {
          ticketInfo: {
            name: fullName,
            type: formData.callType,
            serialNumber: data.SERNUM,
            urgency: formData.urgency,
            ticketResponse: response.data,
          },
        },
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || "שגיאה בשליחת הקריאה",
        severity: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const emailFormatInvalid =
    !!formData.email && !EMAIL_FORMAT_REGEX.test(formData.email);
  const emailErrorMessage =
    fieldErrors.email ||
    (emailFormatInvalid ? "שדה אימייל יכול להכיל כתובת מייל תקינה בלבד" : "");

  const contactEmailFormatInvalid =
    !!contactEmailChangeRequest && !EMAIL_FORMAT_REGEX.test(contactEmailChangeRequest);
  const contactEmailErrorMessage =
    contactFieldErrors.contactEmail ||
    (contactEmailFormatInvalid ? "שדה אימייל יכול להכיל כתובת מייל תקינה בלבד" : "");

  return (
    <>
      <Paper
        sx={{
          p: 2,
          borderRadius: "12px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
        dir="rtl"
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
          {/* <AddCircleIcon sx={{ color: "#2d3436" }} /> */}
          <ArticleIcon sx={{ color: "#2d3436" }} />
          <Typography
            variant="h6"
            sx={{
              fontSize: "1.125rem",
              fontWeight: 500,
              fontFamily: "Rubik, sans-serif",
              color: "#2d3436",
            }}
          >
            פתיחת קריאה
          </Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              required
              fullWidth
              label="שם פרטי"
              value={formData.firstName}
              onChange={handleNameChange("firstName", "שם פרטי")}
              error={!!fieldErrors.firstName}
              helperText={fieldErrors.firstName}
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  backgroundColor: "#fff",
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              required
              fullWidth
              label="שם משפחה"
              value={formData.lastName}
              onChange={handleNameChange("lastName", "שם משפחה")}
              error={!!fieldErrors.lastName}
              helperText={fieldErrors.lastName}
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  backgroundColor: "#fff",
                },
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              label="טלפון"
              value={formData.phone}
              onChange={handlePhoneChange}
              inputProps={{ inputMode: "numeric" }}
              error={!!fieldErrors.phone}
              helperText={fieldErrors.phone}
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  backgroundColor: "#fff",
                },
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              label="אימייל"
              value={formData.email}
              onChange={handleEmailChange}
              error={!!emailErrorMessage}
              helperText={emailErrorMessage}
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  backgroundColor: "#fff",
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth required>
              <InputLabel id="callType-label">סוג קריאה</InputLabel>
              <Select
                labelId="callType-label"
                value={formData.callType}
                label="סוג קריאה"
                onChange={handleChange("callType")}
                sx={{
                  borderRadius: "8px",
                  backgroundColor: "#fff",
                }}
              >
                <MenuItem sx={{ direction: "ltr" }} value="תקלה">
                  תקלה
                </MenuItem>
                <MenuItem sx={{ direction: "ltr" }} value="אחזקה מונעת">
                  אחזקה מונעת
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth required>
              <InputLabel id="urgency-label">דחיפות</InputLabel>
              <Select
                labelId="urgency-label"
                value={formData.urgency}
                label="דחיפות"
                onChange={handleChange("urgency")}
                sx={{
                  borderRadius: "8px",
                  backgroundColor: "#fff",
                }}
              >
                <MenuItem sx={{ direction: "ltr" }} value="לא דחוף">
                  לא דחוף
                </MenuItem>
                <MenuItem sx={{ direction: "ltr" }} value="דחוף">
                  דחוף
                </MenuItem>
                <MenuItem sx={{ direction: "ltr" }} value="מושבת">
                  מושבת
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              multiline
              rows={4}
              label="תיאור תקלה"
              variant="outlined"
              value={formData.text}
              onChange={handleChange("text")}
              placeholder="תיאור הבעיה..."
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  backgroundColor: "#fff",
                },
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.confirmAddress}
                    onChange={handleCheckboxChange("confirmAddress")}
                  />
                }
                label="אני מאשר/ת כי מיקום המכשיר הינו כמפורט לעיל."
              />
              <Typography
                variant="body2"
                onClick={() => setAddressDialogOpen(true)}
                sx={{
                  mr: "34px",
                  color: "#006699",
                  cursor: "pointer",
                  textDecoration: "underline",
                  width: "fit-content",
                }}
              >
                אם הכתובת שגויה, לחצו כאן לעדכון
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.confirmContactPerson}
                    onChange={handleCheckboxChange("confirmContactPerson")}
                  />
                }
                label="אני מאשר/ת כי איש הקשר הרלוונטי לצורך הטיפול בקריאה הינו כמפורט לעיל."
              />
              <Typography
                variant="body2"
                onClick={() => setContactDialogOpen(true)}
                sx={{
                  mr: "34px",
                  color: "#006699",
                  cursor: "pointer",
                  textDecoration: "underline",
                  width: "fit-content",
                }}
              >
                אם הפרטים שגויים, לחצו כאן לעדכון
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  required
                  checked={formData.termsAccepted}
                  onChange={handleCheckboxChange("termsAccepted")}
                />
              }
              label={
                <Typography variant="body2">
                  אני מאשר/ת כי פרטיי יישמרו במאגר מידע של אלדן מקבוצת ניאופרם
                  בכפוף{" "}
                  <a
                    href="https://www.eldan.biz/website-terms-of-use-page/"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#006699" }}
                  >
                    לתקנון
                  </a>{" "}
                  ו
                  <a
                    href="https://www.eldan.biz/privacy-policy/"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#006699" }}
                  >
                    מדיניות הפרטיות
                  </a>{" "}
                  כמפורט{" "}
                  <a
                    href="https://www.eldan.biz/"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#006699" }}
                  >
                    באתר החברה
                  </a>
                </Typography>
              }
            />
          </Grid>
          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "space-between",
                alignItems: "flex-start",
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  style={{ display: "none" }}
                  id="image-upload"
                  onChange={handleImageUpload}
                />
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary" }}
                >
                  ככל ויש ברשותך תמונות של המכשיר התקול אנא צרף/י תמונות{" "}
                  <label
                    htmlFor="image-upload"
                    style={{
                      color: "#006699",
                      cursor: "pointer",
                      textDecoration: "underline",
                    }}
                  >
                    בקישור
                  </label>
                </Typography>
                {formData.images.length > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      flexWrap: "wrap",
                      maxWidth: "300px",
                    }}
                  >
                    {formData.images.map((image, index) => (
                      <Box
                        key={index}
                        sx={{
                          position: "relative",
                        }}
                        onClick={() => handleImageClick(index)}
                      >
                        <img
                          src={URL.createObjectURL(image)}
                          alt={`Uploaded ${index + 1}`}
                          style={{
                            width: 60,
                            height: 60,
                            objectFit: "cover",
                            borderRadius: "4px",
                          }}
                        />
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
              <Button
                variant="contained"
                sx={{
                  backgroundColor: "#006699",
                  borderRadius: "8px",
                  padding: "8px 24px",
                  fontFamily: "Rubik, sans-serif",
                  fontWeight: 500,
                  "&.Mui-disabled": {
                    color: "#333333", // Dark grey color
                  },
                }}
                onClick={handleSave}
                disabled={isSubmitting || !formData.termsAccepted}
              >
                {isSubmitting ? "שולח..." : "אישור"}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirm.open} onClose={handleDeleteCancel} dir="rtl">
        <DialogTitle>מחיקת תמונה</DialogTitle>
        <DialogContent>
          <DialogContentText>
            האם אתה בטוח שברצונך למחוק את התמונה?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            ביטול
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            מחק
          </Button>
        </DialogActions>
      </Dialog>

      {/* Address Change Request Dialog */}
      <Dialog
        open={addressDialogOpen}
        onClose={handleAddressDialogCancel}
        dir="rtl"
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>עדכון כתובת האתר</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            מה הכתובת הנכונה?
          </DialogContentText>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {ADDRESS_FIELDS.map(({ key, label, isNumeric, required }, index) => (
              <TextField
                key={key}
                autoFocus={index === 0}
                required={required}
                fullWidth
                label={label}
                value={addressChangeRequest[key]}
                onChange={handleAddressFieldChange(key, label, isNumeric)}
                inputProps={isNumeric ? { inputMode: "numeric" } : undefined}
                error={!!addressFieldErrors[key]}
                helperText={addressFieldErrors[key]}
                variant="outlined"
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAddressDialogCancel} color="primary">
            ביטול
          </Button>
          <Button
            onClick={handleAddressDialogSave}
            color="primary"
            variant="contained"
            disabled={ADDRESS_FIELDS.some(
              ({ key }) => !!addressFieldErrors[key] && addressFieldErrors[key] !== "שדה חובה"
            )}
          >
            שמירה
          </Button>
        </DialogActions>
      </Dialog>

      {/* Contact Person Change Request Dialog */}
      <Dialog
        open={contactDialogOpen}
        onClose={handleContactDialogCancel}
        dir="rtl"
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>עדכון איש קשר</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            מי איש הקשר הנכון?
          </DialogContentText>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              autoFocus
              required
              fullWidth
              label="שם איש קשר"
              value={contactNameChangeRequest}
              onChange={handleContactNameChangeInput}
              error={!!contactFieldErrors.contactName}
              helperText={contactFieldErrors.contactName}
              variant="outlined"
            />
            <TextField
              required
              fullWidth
              label="טלפון"
              value={contactPhoneChangeRequest}
              onChange={handleContactPhoneChangeInput}
              inputProps={{ inputMode: "numeric" }}
              error={!!contactFieldErrors.contactPhone}
              helperText={contactFieldErrors.contactPhone}
              variant="outlined"
            />
            <TextField
              required
              fullWidth
              label="אימייל"
              value={contactEmailChangeRequest}
              onChange={handleContactEmailChangeInput}
              error={!!contactEmailErrorMessage}
              helperText={contactEmailErrorMessage}
              variant="outlined"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleContactDialogCancel} color="primary">
            ביטול
          </Button>
          <Button
            onClick={handleContactDialogSave}
            color="primary"
            variant="contained"
            disabled={
              (!!contactFieldErrors.contactName && contactFieldErrors.contactName !== "שדה חובה") ||
              (!!contactFieldErrors.contactPhone && contactFieldErrors.contactPhone !== "שדה חובה") ||
              contactEmailFormatInvalid
            }
          >
            שמירה
          </Button>
        </DialogActions>
      </Dialog>

      <SnackbarAlert
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        duration={5000}
        onClose={handleCloseSnackbar}
      />
    </>
  );
}

export default OpeningReading;
